import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { services } from '../mock';
import SEO from '../components/SEO';
import * as Icons from 'lucide-react';

const Services = ({ onCtaClick }) => {
  const colors = [
    { gradient: 'from-blue-500 to-cyan-500', border: 'border-blue-200', bg: 'from-blue-50 to-cyan-50' },
    { gradient: 'from-purple-500 to-pink-500', border: 'border-purple-200', bg: 'from-purple-50 to-pink-50' },
    { gradient: 'from-orange-500 to-red-500', border: 'border-orange-200', bg: 'from-orange-50 to-red-50' },
    { gradient: 'from-green-500 to-emerald-500', border: 'border-green-200', bg: 'from-green-50 to-emerald-50' },
    { gradient: 'from-indigo-500 to-purple-500', border: 'border-indigo-200', bg: 'from-indigo-50 to-purple-50' },
    { gradient: 'from-pink-500 to-rose-500', border: 'border-pink-200', bg: 'from-pink-50 to-rose-50' },
    { gradient: 'from-yellow-500 to-orange-500', border: 'border-yellow-200', bg: 'from-yellow-50 to-orange-50' },
    { gradient: 'from-teal-500 to-cyan-500', border: 'border-teal-200', bg: 'from-teal-50 to-cyan-50' },
    { gradient: 'from-violet-500 to-purple-500', border: 'border-violet-200', bg: 'from-violet-50 to-purple-50' },
    { gradient: 'from-fuchsia-500 to-pink-500', border: 'border-fuchsia-200', bg: 'from-fuchsia-50 to-pink-50' },
  ];

  return (
    <div className="bg-white text-gray-900 min-h-screen">
      <SEO 
        title="Our Services - IlinkTrading | Web Development & Digital Solutions"
        description="Comprehensive digital services including web development, blockchain solutions, mobile apps, UI/UX design, and digital marketing. Custom solutions for your business."
        keywords="web development services, blockchain development, mobile app development, UI/UX design, digital marketing, custom software, e-commerce development"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" />
        <div className="absolute top-40 left-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md mb-6">
            <Sparkles className="text-purple-600" size={18} />
            <span className="text-sm font-semibold text-gray-700">Our Expertise</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Comprehensive Digital Solutions
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Elevate your business with our full-service digital expertise. From development to deployment,
            we deliver excellence at every step.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = Icons[service.icon];
              const color = colors[index % colors.length];

              return (
                <div
                  key={service.id}
                  className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 ${color.border} hover:-translate-y-2`}
                >
                  {/* Gradient Header */}
                  <div className={`h-2 bg-gradient-to-r ${color.gradient}`} />
                  
                  <div className="p-8">
                    {/* Icon */}
                    <div className={`w-16 h-16 bg-gradient-to-br ${color.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {IconComponent && <IconComponent className="text-white" size={32} strokeWidth={2} />}
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                    {/* Features List */}
                    <div className="space-y-3 mb-6">
                      {service.features.slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${color.gradient} flex items-center justify-center flex-shrink-0`}>
                            <Check className="text-white" size={12} strokeWidth={3} />
                          </div>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTAs */}
                    <div className="flex gap-3">
                      <Link
                        to={`/services/${service.slug}`}
                        className={`flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r ${color.gradient} text-white px-6 py-3 rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-300 group-hover:gap-3`}
                      >
                        Learn More
                        <ArrowRight size={16} />
                      </Link>
                      <button
                        onClick={() => onCtaClick && onCtaClick(service.title)}
                        className="px-6 py-3 rounded-xl text-sm font-semibold border-2 border-gray-200 hover:border-blue-500 text-gray-700 hover:text-blue-600 transition-all duration-300"
                      >
                        Get Quote
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that delivers exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understanding your goals and requirements', gradient: 'from-blue-500 to-cyan-500' },
              { step: '02', title: 'Planning', desc: 'Strategic roadmap and technical architecture', gradient: 'from-purple-500 to-pink-500' },
              { step: '03', title: 'Development', desc: 'Agile development with regular updates', gradient: 'from-orange-500 to-red-500' },
              { step: '04', title: 'Launch', desc: 'Deployment, testing, and ongoing support', gradient: 'from-green-500 to-emerald-500' }
            ].map((item, idx) => (
              <div key={idx} className="text-center group">
                <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <span className="text-3xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            Ready to Elevate Your Business?
          </h2>
          <p className="text-xl text-white/90">
            Let's discuss how our services can help you achieve your goals
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => onCtaClick && onCtaClick()}
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl text-base font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Start Your Project
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;